import { useEffect, useState } from "react";
import Lenis from "lenis";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Instagram, MessageCircle, Menu, X, LogOut, LayoutDashboard } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSite } from "../context/SiteContext";

const NAV = [
  { to: "/", label: "Início" },
  { to: "/reflexoes", label: "Reflexões" },
  { to: "/produtos", label: "Produtos" },
  { to: "/comunidade", label: "Comunidade" },
  { to: "/contato", label: "Contato" },
];

function Header() {
  const { user, logout } = useAuth();
  const { site } = useSite();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 border-b border-gold/15 bg-black/60 backdrop-blur-xl" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-12">
        <Link to="/" className="flex items-center gap-3" data-testid="header-brand-link">
          <span className="flex h-10 w-10 items-center justify-center rounded-full border border-gold/50 font-serif text-sm text-gold">
            REF
          </span>
          <span className="font-serif text-lg tracking-[0.25em] text-zinc-100">{site.brand_name}</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="header-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "")}`}
              className={({ isActive }) =>
                `link-underline text-xs font-semibold uppercase tracking-[0.2em] transition-colors ${
                  isActive ? "text-gold" : "text-zinc-400 hover:text-zinc-100"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link to="/admin" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-gold" data-testid="nav-admin-link">
                  <LayoutDashboard size={14} /> Painel
                </Link>
              )}
              <button onClick={handleLogout} className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-gold" data-testid="logout-button">
                <LogOut size={14} /> Sair
              </button>
            </>
          ) : (
            <Link to="/entrar" className="btn-gold !px-6 !py-2.5" data-testid="nav-login-button">
              Entrar
            </Link>
          )}
        </div>

        <button className="text-zinc-200 lg:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <div className="border-t border-gold/15 bg-black/95 px-6 py-6 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col gap-5">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                className="text-sm font-semibold uppercase tracking-[0.2em] text-zinc-300"
              >
                {item.label}
              </NavLink>
            ))}
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="text-sm uppercase tracking-[0.2em] text-gold">
                    Painel Admin
                  </Link>
                )}
                <button onClick={handleLogout} className="text-left text-sm uppercase tracking-[0.2em] text-zinc-400" data-testid="mobile-logout-button">
                  Sair
                </button>
              </>
            ) : (
              <Link to="/entrar" onClick={() => setOpen(false)} className="text-sm uppercase tracking-[0.2em] text-gold" data-testid="mobile-login-link">
                Entrar
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const { site } = useSite();
  return (
    <footer className="border-t border-gold/15 bg-[#050505]" data-testid="site-footer">
      <div className="mx-auto grid max-w-7xl gap-12 px-6 py-16 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/50 font-serif text-gold">REF</span>
            <span className="font-serif text-xl tracking-[0.25em] text-zinc-100">{site.brand_name}</span>
          </div>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-zinc-400">{site.tagline}. Para quem quer sair do automático.</p>
        </div>
        <div className="lg:col-span-3">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Navegação</p>
          <div className="mt-6 flex flex-col gap-3">
            {NAV.map((item) => (
              <Link key={item.to} to={item.to} className="text-sm text-zinc-400 transition-colors hover:text-gold">
                {item.label}
              </Link>
            ))}
          </div>
        </div>
        <div className="lg:col-span-3">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Conexão</p>
          <div className="mt-6 flex gap-4">
            <a href={site.instagram_url} target="_blank" rel="noreferrer" className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-all hover:-translate-y-1 hover:border-gold/60" data-testid="footer-instagram-link" aria-label="Instagram">
              <Instagram size={18} />
            </a>
            <a href={`https://wa.me/${site.whatsapp}`} target="_blank" rel="noreferrer" className="flex h-11 w-11 items-center justify-center border border-gold/25 text-gold transition-all hover:-translate-y-1 hover:border-gold/60" data-testid="footer-whatsapp-link" aria-label="WhatsApp">
              <MessageCircle size={18} />
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-gold/10 py-6 text-center text-xs tracking-[0.2em] text-zinc-600">
        © {new Date().getFullYear()} {site.brand_name} — TODOS OS DIREITOS RESERVADOS
      </div>
    </footer>
  );
}

export default function Layout({ children }) {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] font-sans text-zinc-200 antialiased">
      <div className="grain-overlay" aria-hidden="true" />
      <Header />
      <main>{children}</main>
      <Footer />
    </div>
  );
}
